import { useEffect, useRef, useState } from "react";
import { Empty, Tag } from "antd";
import { FaArrowRight } from "react-icons/fa";
import CollapseItems from "../collapse-component";
import {
  StyledCollapse,
  StyledCollapseAdd,
  StyledCollapseBody,
  StyledCollapseBorder,
  StyledCollapseHeader,
  StyledCollapseHeaderBorder,
  StyledCollapseHeaderButtons,
  StyledCollapseHeaderContent,
  StyledCollapseHeaderIcon,
} from "../index.styled";
import {
  collapseColorClassWithIndex,
  Observer,
  onClickListener,
  toggleCollapse,
  toggleCollapseHandler,
} from "../utils";
import { TTelegramBotDialogueRow } from "@lib/types/TelegramChatBot";
import useIntl from "react-intl/lib/src/components/useIntl";
import { useDisclosure } from "@lib/hooks/useDisclosure";
import { useMutation } from "@tanstack/react-query";
import { deleteOneTelegramBotDialogue } from "@lib/api/telegramBotDialogue";
import { TruncateString } from "@lib/components/MiniComponents/Table/TruncateString";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { toast } from "react-toastify";
import CollapseItemForm from "../collapse-item-form";

interface ICollapseItemCompProp {
  item: TTelegramBotDialogueRow;
  parentIndex: number;
  refetchDialogue?: () => void;
  requireAuth?: boolean;
}

const CollapseItemComponent = ({
  item,
  parentIndex,
  refetchDialogue,
  requireAuth,
}: ICollapseItemCompProp) => {
  // const { t: translate } = useTranslation();
  const { messages } = useIntl();
  const collapseRef = useRef<HTMLDivElement>(null);
  const borderRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [observer, setObserver] = useState<MutationObserver | null>(null);
  const { isOpen: isAddNewDialogueOpen, toggle: toggleAddNewDialogue } =
    useDisclosure();
  const [editItem, setEditItem] = useState<TTelegramBotDialogueRow | null>(
    null,
  );

  const {
    mutate: deleteTelegramBotDialogue,
    isPending: isDeleteTelegramBotDialogueLoading,
  } = useMutation({
    mutationFn: (id: string) => deleteOneTelegramBotDialogue(id),
    onSuccess: () => {
      refetchDialogue?.();
      toast.success(messages["action.success"] as string);
    },
  });

  const handleDeleteDialogue = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    deleteTelegramBotDialogue(item.id);
  };

  const toggleAddDialogue = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const target = event.target as HTMLElement;
    toggleCollapse(target, true);
    toggleAddNewDialogue();
  };

  useEffect(() => {
    if (!observer) {
      const collapse = collapseRef.current;
      if (collapse) {
        const _observer = Observer({ borderRef, bodyRef });

        _observer.observe(collapse, {
          childList: true,
          subtree: true,
          attributes: true,
        });

        setObserver(_observer);
      }
    }

    document.addEventListener("click", onClickListener, true);

    return () => {
      if (observer) observer.disconnect();
      document.removeEventListener("click", onClickListener);
    };
  }, [collapseRef, borderRef, bodyRef]);

  return (
    <StyledCollapse
      className={`Collapse ${collapseColorClassWithIndex(parentIndex, item?.children?.length)}`}
      ref={collapseRef}
      onClick={toggleCollapseHandler}
    >
      <StyledCollapseBorder className="Collapse-Border" ref={borderRef} />
      <StyledCollapseHeader
        className={`Collapse-Header ${editItem && "Collapse-Header-Edit"}`}
      >
        <StyledCollapseHeaderBorder className="Collapse-Header-Border" />
        <StyledCollapseHeaderIcon className="Collapse-Header-Icon">
          <FaArrowRight />
        </StyledCollapseHeaderIcon>
        {editItem ? (
          <CollapseItemForm
            mode="edit"
            dialogue={editItem}
            refetchDialogue={refetchDialogue}
            requireAuth={requireAuth}
            closeModal={() => {
              setEditItem(null);
            }}
          />
        ) : (
          <>
            <StyledCollapseHeaderContent>
              <TruncateString str={item.title} maxLength={150} />
              <div
                style={{
                  display: "flex",
                  gap: 3,
                  marginTop: 3,
                  flexWrap: "wrap",
                }}
              >
                {item?.options
                  ?.filter(
                    (option) => option[0]?.callback_data !== item.parentId,
                  )
                  ?.map((option, index) => (
                    <Tag key={`${option[0]?.text}-${index}`}>
                      {option[0]?.text}
                    </Tag>
                  ))}
              </div>
            </StyledCollapseHeaderContent>
            <StyledCollapseHeaderButtons className="Collapse-Header-Buttons">
              <ActionButton
                loading={isDeleteTelegramBotDialogueLoading}
                onClick={handleDeleteDialogue}
                type="delete"
                noLabel
                itemName={item.title}
              />
              <ActionButton
                onClick={(e) => {
                  e.stopPropagation();
                  setEditItem(item);
                }}
                type="edit"
                noLabel
              />
              <ActionButton onClick={toggleAddDialogue} type="add" noLabel />
            </StyledCollapseHeaderButtons>
          </>
        )}
      </StyledCollapseHeader>
      <StyledCollapseBody className="Collapse-Body" ref={bodyRef}>
        {item?.children && item.children?.length > 0 && (
          <CollapseItems
            parentIndex={parentIndex + 1}
            data={item.children}
            refetchDialogue={refetchDialogue}
            requireAuth={item.requireAuth}
          />
        )}

        {!item?.children?.length && !isAddNewDialogueOpen && (
          <Empty className="Empty" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}

        {isAddNewDialogueOpen && (
          <StyledCollapseAdd className="Collapse-Add">
            <CollapseItemForm
              mode="add"
              requireAuth={requireAuth}
              parentId={item.id}
              refetchDialogue={refetchDialogue}
              closeModal={(event) => {
                if (event) event.stopPropagation();
                toggleAddNewDialogue();
              }}
            />
          </StyledCollapseAdd>
        )}
      </StyledCollapseBody>
    </StyledCollapse>
  );
};

export default CollapseItemComponent;
